<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div>
        <!-- Header -->
        <div class="mb-8 flex flex-col items-center">
          <Title variant="indigo" align="center">Cohort Dashboard</Title>
          <div class="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
            <span class="text-gray-500">Cohort</span>
            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ cohortFiles.length }} Files</span>
            <span class="text-gray-300">•</span>
            <span class="text-gray-500">Status</span>
            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ getStatusText() }}</span>
          </div>
        </div>

        <!-- Cohort Overview Card -->
        <div class="bg-white rounded-lg shadow border-4 border-gray-300 p-6 mb-8 w-full max-w-7xl mx-auto">
          <!-- Cohort Files Summary - Only show before processing -->
          <div v-if="!processingResults" class="mb-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Cohort Files Summary</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Total Files -->
              <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
                <div class="flex flex-col items-center text-center">
                  <div class="mb-1">
                    <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Total Files</h3>
                    <p class="text-lg font-bold text-blue-600">{{ cohortFiles.length }}</p>
                  </div>
                </div>
              </div>

              <!-- Total Subjects -->
              <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
                <div class="flex flex-col items-center text-center">
                  <div class="mb-1">
                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Unique Subjects</h3>
                    <p class="text-lg font-bold text-green-600">{{ uniqueSubjects.length }}</p>
                  </div>
                </div>
              </div>

              <!-- Processed -->
              <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
                <div class="flex flex-col items-center text-center">
                  <div class="mb-1">
                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Processed</h3>
                    <p class="text-lg font-bold text-green-600">{{ processingResults?.summary_stats?.successful_subjects || 0 }}</p>
                  </div>
                </div>
              </div>

              <!-- Failed -->
              <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
                <div class="flex flex-col items-center text-center">
                  <div class="mb-1">
                    <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-gray-900">Failed</h3>
                    <p class="text-lg font-bold text-red-600">{{ processingResults?.summary_stats?.failed_subjects || 0 }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Processing Section -->
          <div class="border-t border-gray-200 pt-6 mb-6">
            <div class="flex justify-center">
              <div class="w-full max-w-7xl">
                <!-- Show Process button when not processing -->
                <div v-if="!showStatusBar" class="flex justify-center">
                  <button @click="showProcessSettingsModal = true" class="w-[85%] group bg-gray-700 border-2 border-gray-500 rounded-lg shadow-lg hover:shadow-xl hover:border-blue-400 transition-all duration-300 p-6">
                    <div class="flex items-center justify-center text-center">
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-200">
                          <svg class="w-5 h-5 text-blue-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                          </svg>
                        </div>
                      </div>
                      <div class="px-4">
                        <h3 class="text-2xl font-semibold text-white mb-1 group-hover:text-blue-300 transition-colors duration-200">Process & Analyze Cohort</h3>
                        <p class="text-base text-gray-300 group-hover:text-gray-200">Configure processing settings and run analysis for all cohort files</p>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-200">
                          <svg class="w-5 h-5 text-blue-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>

                <!-- Show Processing when in progress -->
                <div v-if="showStatusBar && processing" class="w-full">
                  <!-- Overall Progress -->
                  <div class="flex justify-center mb-4">
                    <div class="bg-gray-700 border-2 border-gray-500 rounded-lg shadow-lg p-6">
                      <div class="flex items-center justify-center mb-4">
                        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400 mr-4"></div>
                        <span class="text-xl font-semibold text-white">Processing Cohort</span>
                      </div>
                      
                      <!-- Progress Stats -->
                      <div class="grid grid-cols-3 gap-4 text-center text-white">
                        <div>
                          <div class="text-2xl font-bold text-blue-400">{{ processingProgress.completed }}</div>
                          <div class="text-sm">Completed</div>
                        </div>
                        <div>
                          <div class="text-2xl font-bold text-yellow-400">{{ processingProgress.failed }}</div>
                          <div class="text-sm">Failed</div>
                        </div>
                        <div>
                          <div class="text-2xl font-bold text-gray-300">{{ processingProgress.total }}</div>
                          <div class="text-sm">Total</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Current Subject -->
                  <div v-if="processingProgress.current" class="flex justify-center">
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div class="flex items-center">
                        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                        <span class="text-blue-800 font-medium">Processing Subject {{ processingProgress.current }}...</span>
                      </div>
                    </div>
                  </div>

                </div>

                <!-- Show Results when processing is complete -->
                <div v-if="showStatusBar && !processing && processingResults" class="w-full">
                  <div class="flex flex-col items-center text-center">
                    <!-- Action Buttons -->
                    <div class="flex justify-center items-center gap-4 w-full max-w-none mx-auto">
                      <button
                        @click="showCurveResults"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Curve Results</span>
                        </div>
                      </button>
                      
                      <button
                        @click="showDailyTracking"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Daily Tracking</span>
                        </div>
                      </button>
                      
                      <button
                        @click="downloadCohortResults"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Download Results</span>
                        </div>
                      </button>
                      
                      <button
                        @click="rerunCohortAnalysis"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Rerun Analysis</span>
                        </div>
                      </button>
                    </div>

                    <!-- Processing Summary -->
                    <div class="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 w-full max-w-2xl">
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div class="text-center">
                          <div class="text-2xl font-bold text-green-600">{{ processingResults.summary_stats.successful_subjects }}</div>
                          <div class="text-sm text-green-700">Processed</div>
                        </div>
                        <div class="text-center">
                          <div class="text-2xl font-bold text-red-600">{{ processingResults.summary_stats.failed_subjects }}</div>
                          <div class="text-sm text-red-700">Failed</div>
                        </div>
                        <div class="text-center">
                          <div class="text-2xl font-bold text-blue-600">{{ processingResults.total_subjects }}</div>
                          <div class="text-sm text-blue-700">Total Subjects</div>
                        </div>
                      </div>

                      <!-- Failed Subjects -->
                      <div v-if="processingResults.failed_subjects.length > 0" class="mt-4">
                        <h4 class="text-md font-semibold text-red-800 mb-3">Failed Subjects</h4>
                        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div v-for="subject in processingResults.failed_subjects" :key="subject.subid" class="mb-2">
                            <div class="text-sm text-red-800">
                              <span class="font-medium">{{ subject.subid }}:</span> {{ subject.error }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cohort Files Details -->
          <div class="border-t border-gray-200 pt-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Cohort Files Details</h3>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject ID</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dataset ID</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Type</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="file in sortedCohortFiles" :key="file.filename" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ file.filename }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ file.subid }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ file.datasetId }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ getFileType(file.filename) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span 
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getFileStatusClass(file)"
                      >
                        {{ getFileStatus(file) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Process & Analyze Cohort Modal -->
    <ProcessDataModal
      :isVisible="showProcessSettingsModal"
      :processing="processing"
      :processingSettings="processingSettings"
      @close="showProcessSettingsModal = false"
      @process="handleProcessData"
    />

    <!-- Download Data Modal -->
    <BaseModal :isVisible="showDownloadModal" @close="showDownloadModal = false" width="lg">
      <template #header>Download Cohort Results</template>
      <template #content>
        <p class="text-sm text-gray-500 mb-6">Click the download button next to each data type to download as an Excel file</p>
        
        <div class="space-y-4">
          <!-- Curve Features -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="hasCurveFeatures">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Curve Features
              </label>
              <span class="ml-2 text-xs text-gray-500">({{ totalCurveFeatures }} curves)</span>
            </div>
            <button
              @click="downloadCurveFeatures"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Curve Features"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Day Level Data -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="hasDayLevelData">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Day Level Data
              </label>
              <span class="ml-2 text-xs text-gray-500">({{ totalDayLevelData }} days)</span>
            </div>
            <button
              @click="downloadDayLevelData"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Day Level Data"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Processing Summary -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="processingResults?.summary_stats">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Processing Summary
              </label>
            </div>
            <button
              @click="downloadProcessingSummary"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Processing Summary"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <div class="mt-6 text-xs text-gray-500">
          <p class="text-orange-600"><strong>Note:</strong> Processed datasets are not included in cohort downloads due to memory constraints.</p>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="showDownloadModal = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import ProcessDataModal from '../../components/ProcessDataModal.vue'
import BaseModal from '../../components/BaseModal.vue'
import Title from '../../components/Title.vue'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import { 
  calculateTotalCurveFeatures,
  calculateTotalDayLevelData,
  cohortHasCurveFeatures,
  cohortHasDayLevelData,
  compileCohortDataForDownload
} from '../../utils/excelDownloadUtils.js'
import { downloadCohortProcessingSummary } from '../../utils/processingSummaryExportUtils.js'

export default {
  name: 'CohortDashboard',
  inject: ['globalState', 'updateGlobalState', 'setBackButton', 'hideBackButton'],
  components: {
    ProcessDataModal,
    BaseModal,
    Title
  },
  data() {
    return {
      showProcessSettingsModal: false,
      showDownloadModal: false,
      processing: false,
      showStatusBar: false,
      processingResults: null,
      subjectResults: {},
      columnMetadata: {}, // Store column ordering metadata per subject
      cohortColumnMetadata: null, // Store aggregated column metadata for downloads
      processingProgress: {
        total: 0,
        completed: 0,
        failed: 0,
        current: null
      },
      processingSettings: {
        data_timezone: 'US/Central',
        adjust_for_gaps_and_non_wear: true,
        smooth_and_impute: true,
        identify_curves: true,
        analyze_days: true,
        match_events_to_curves: false,
        gaps_and_non_wear_attrs: {},
        smooth_and_impute_attrs: {},
        curve_attrs: {
          curve_threshold: 'auto',
          manual_threshold: 10,
          default_threshold: 10,  // Max threshold permitted for auto mode (8-15)
          merge_curves_within_duration: 2
        },
        day_attrs: {
          day_start_hour: 0,
          enable_drinking_day_algorithm: false,
          day_export_columns: {}
        },
        event_attrs: {}
      }
    }
  },
  computed: {
    cohortFiles() {
      return this.globalState?.cohortFiles || []
    },
    uniqueSubjects() {
      const subjects = new Set()
      this.cohortFiles.forEach(file => {
        subjects.add(file.subid)
      })
      return Array.from(subjects)
    },
    sortedCohortFiles() {
      return [...this.cohortFiles].sort((a, b) => {
        // Sort by Subject ID (numerically)
        const subidA = parseInt(a.subid)
        const subidB = parseInt(b.subid)
        
        if (subidA !== subidB) {
          return subidA - subidB
        }
        
        // If SubIDs are the same, sort by Dataset ID (numerically)
        const datasetA = parseInt(a.datasetId)
        const datasetB = parseInt(b.datasetId)
        return datasetA - datasetB
      })
    },
    hasCurveFeatures() {
      return cohortHasCurveFeatures(this.processingResults?.processed_subjects)
    },
    hasDayLevelData() {
      return cohortHasDayLevelData(this.processingResults?.processed_subjects)
    },
    totalCurveFeatures() {
      return calculateTotalCurveFeatures(this.processingResults?.processed_subjects)
    },
    totalDayLevelData() {
      return calculateTotalDayLevelData(this.processingResults?.processed_subjects)
    }
  },
  methods: {
    getStatusText() {
      if (this.processing) {
        return 'Processing in Progress'
      } else if (this.processingResults) {
        return 'Cohort Processed'
      } else {
        return 'Ready for Processing'
      }
    },

    getFileType(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      if (extension === 'xlsx') return 'Excel'
      if (extension === 'csv') return 'CSV'
      return extension.toUpperCase()
    },

    getFileStatus(file) {
      const subid = file.subid
      
      // Check if currently being processed
      if (this.processingProgress.current === subid) {
        return 'In Progress'
      }
      
      // Check if processing is complete
      if (this.subjectResults[subid]) {
        if (this.subjectResults[subid].status === 'success') {
          return 'Processed'
        } else if (this.subjectResults[subid].status === 'failed') {
          return 'Failed'
        }
      }
      
      // Default status
      return 'Ready'
    },

    getFileStatusClass(file) {
      const subid = file.subid
      
      // Check if currently being processed
      if (this.processingProgress.current === subid) {
        return 'bg-blue-100 text-blue-800'
      }
      
      // Check if processing is complete
      if (this.subjectResults[subid]) {
        if (this.subjectResults[subid].status === 'success') {
          return 'bg-green-100 text-green-800'
        } else if (this.subjectResults[subid].status === 'failed') {
          return 'bg-red-100 text-red-800'
        }
      }
      
      // Default status
      return 'bg-green-100 text-green-800'
    },

    handleProcessData(settings) {
      // This method is called by the ProcessDataModal component
      this.processingSettings = { ...this.processingSettings, ...settings }
      // Reset the state when processing actually begins
      this.showStatusBar = false
      this.processing = false
      this.processingResults = null
      this.processData()
    },

    async processData() {
      console.log('🚀 Starting progressive cohort processing with settings:', this.processingSettings)
      
      // Close modal and show status bar
      this.showProcessSettingsModal = false
      this.showStatusBar = true
      this.processing = true
      this.processingResults = null

      // Initialize progressive results tracking
      this.subjectResults = {}
      this.processingProgress = {
        total: this.uniqueSubjects.length,
        completed: 0,
        failed: 0,
        current: null
      }

      try {
        // Group files by subject
        const subjectGroups = this.groupFilesBySubject()
        console.log('📊 Grouped files by subject:', Object.keys(subjectGroups))

        // Process each subject individually
        for (const [subid, subjectFiles] of Object.entries(subjectGroups)) {
          console.log(`👤 Processing subject ${subid} (${subjectFiles.length} files)`)
          
          // Update progress
          this.processingProgress.current = subid
          
          try {
            // Prepare subject files data for backend
            // We need to send the actual file content, not just file paths
            const subjectFilesData = []
            
            for (const file of subjectFiles) {
              // Convert Excel files to CSV on frontend, just like CohortUpload does
              const metadata = {
                subid: file.subid,
                datasetId: file.datasetId
              }
              
              // Import the Excel converter
                const { processFileForUpload } = await import('../../utils/excelConverter.js')
              const processedFile = await processFileForUpload(file.file, metadata)
              
              // Read the processed file content as text (it's now CSV)
              const fileContent = await this.readFileContent(processedFile)
              subjectFilesData.push({
                filename: processedFile.name, // Use the converted filename
                content: fileContent,
                subid: file.subid,
                dataset_id: file.datasetId
              })
            }

            console.log('📤 Sending subject files data:', {
              subid: subjectFiles[0].subid,
              fileCount: subjectFilesData.length,
              filenames: subjectFilesData.map(f => f.filename)
            })

            // Make API call to process this subject
            const response = await axios.post(buildApiUrl(API_ENDPOINTS.PROCESS_COHORT_SUBJECT), {
              subject_files: subjectFilesData,
              processing_settings: this.processingSettings
            }, {
              withCredentials: true
            })

            // Parse response data if it's a string
            let responseData = response.data
            if (typeof responseData === 'string') {
              try {
                responseData = JSON.parse(responseData)
                console.log(`🔧 Parsed JSON string response for subject ${subid}`)
              } catch (parseError) {
                console.error(`❌ Failed to parse JSON response for subject ${subid}:`, parseError)
                throw new Error(`Invalid response format for subject ${subid}`)
              }
            }

            // Debug response structure
            console.log(`🔍 Response for subject ${subid}:`, {
              status: response.status,
              data: responseData,
              success: responseData.success,
              successType: typeof responseData.success,
              message: responseData.message,
              hasData: !!responseData.data
            })

            if (responseData.success) {
              // Store subject results
              this.subjectResults[subid] = {
                status: 'success',
                data: responseData.data
              }
              
              // Store column metadata for this subject
              if (responseData.data && responseData.data.column_metadata) {
                this.columnMetadata[subid] = responseData.data.column_metadata
                console.log(`📊 Stored column metadata for subject ${subid}:`, responseData.data.column_metadata)
              }
              
              this.processingProgress.completed++
              console.log(`✅ Subject ${subid} processed successfully`)
            } else {
              throw new Error(responseData.message || `Subject ${subid} processing failed`)
            }
            
          } catch (error) {
            console.error(`❌ Error processing subject ${subid}:`, error)
            this.subjectResults[subid] = {
              status: 'failed',
              error: error.message || 'Processing failed'
            }
            this.processingProgress.failed++
          }
        }

        // Compile final cohort results
        this.processingResults = this.compileCohortResults()
        
        // Store results in global state
        this.updateGlobalState({
          cohortProcessingResults: this.processingResults
        })
        
        console.log('🎉 Progressive cohort processing completed:', this.processingResults)
        
      } catch (error) {
        console.error('❌ Error during cohort processing:', error)
        this.processingResults = {
          success: false,
          error: error.message || 'Failed to process cohort data'
        }
      } finally {
        this.processing = false
        this.processingProgress.current = null
      }
    },

    groupFilesBySubject() {
      const groups = {}
      this.cohortFiles.forEach(file => {
        const subid = file.subid
        if (!groups[subid]) {
          groups[subid] = []
        }
        groups[subid].push(file)
      })
      return groups
    },

    async readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        
        // After Excel conversion, all files are now CSV
        reader.readAsText(file)
      })
    },

    compileCohortResults() {
      const successfulSubjects = []
      const failedSubjects = []
      
      Object.entries(this.subjectResults).forEach(([subid, result]) => {
        if (result.status === 'success') {
          successfulSubjects.push(result.data)
        } else {
          failedSubjects.push({
            subid: subid,
            error: result.error
          })
        }
      })

      // Aggregate column metadata from all successful subjects
      this.cohortColumnMetadata = this.aggregateColumnMetadata()

      return {
        cohort_processing_status: 'completed',
        total_subjects: this.uniqueSubjects.length,
        processed_subjects: successfulSubjects,
        failed_subjects: failedSubjects,
        summary_stats: {
          total_files: this.cohortFiles.length,
          unique_subjects: this.uniqueSubjects.length,
          successful_subjects: successfulSubjects.length,
          failed_subjects: failedSubjects.length,
          processing_end_time: new Date().toISOString()
        }
      }
    },

    aggregateColumnMetadata() {
      // Aggregate column metadata from all subjects to determine the most complete ordering
      const aggregatedMetadata = {
        curve_features_order: [],
        day_level_data_order: []
      }

      // Collect all column orders from successful subjects
      const allCurveOrders = []
      const allDayOrders = []
      
      Object.values(this.columnMetadata).forEach(metadata => {
        if (metadata.curve_features_order && metadata.curve_features_order.length > 0) {
          allCurveOrders.push(metadata.curve_features_order)
        }
        if (metadata.day_level_data_order && metadata.day_level_data_order.length > 0) {
          allDayOrders.push(metadata.day_level_data_order)
        }
      })

      // Use the longest/most complete column order as the template
      if (allCurveOrders.length > 0) {
        aggregatedMetadata.curve_features_order = allCurveOrders.reduce((longest, current) => 
          current.length > longest.length ? current : longest
        )
      }

      if (allDayOrders.length > 0) {
        aggregatedMetadata.day_level_data_order = allDayOrders.reduce((longest, current) => 
          current.length > longest.length ? current : longest
        )
      }

      console.log('📊 Aggregated column metadata:', aggregatedMetadata)
      return aggregatedMetadata
    },

    showCurveResults() {
      // TODO: Implement curve results functionality for cohort
      console.log('Curve Results clicked - cohort curve results functionality to be implemented')
    },

    showDailyTracking() {
      // TODO: Implement daily tracking functionality for cohort
      console.log('Daily Tracking clicked - cohort daily tracking functionality to be implemented')
    },

    downloadCohortResults() {
      this.showDownloadModal = true
    },

    // Individual download methods using client-side Excel generation
    // NOTE: These methods use client-side XLSX generation for datasets that fit in memory.
    // For large datasets stored in S3 (where data_stored_in_s3 === true), use the
    // /api/download-data endpoint instead to fetch data from S3 and generate ZIP archives.
    downloadCurveFeatures() {
      // NOTE: For large datasets stored in S3, use /api/download-data endpoint instead.
      try {
        if (!this.hasCurveFeatures) {
          console.warn('No curve features available to download')
          return
        }

        // Compile cohort curve features data
        const downloadOptions = { curve_features: true, day_level_data: false, processing_summary: false }
        const compiledData = compileCohortDataForDownload(
          {
            summary_stats: this.processingResults?.summary_stats,
            processed_subjects: this.processingResults?.processed_subjects || [],
            failed_subjects: this.processingResults?.failed_subjects || [],
            cohort_files: this.cohortFiles,
            processing_settings: this.processingSettings
          },
          downloadOptions,
          this.cohortColumnMetadata
        )

        if (!compiledData.curve_features || compiledData.curve_features.length === 0) {
          console.warn('No curve features data compiled')
          return
        }

        // Convert array to worksheet
        const worksheet = XLSX.utils.json_to_sheet(compiledData.curve_features)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Curve Features')

        // Generate filename
        const cohortId = `cohort_${new Date().toISOString().split('T')[0]}`
        const filename = `${cohortId}_curve_features.xlsx`

        // Download the file
        XLSX.writeFile(workbook, filename)
        console.log('✅ Cohort curve features downloaded:', filename)
      } catch (error) {
        console.error('❌ Failed to download cohort curve features:', error)
        alert('Failed to download curve features. Please try again.')
      }
    },

    downloadDayLevelData() {
      // NOTE: For large datasets stored in S3, use /api/download-data endpoint instead.
      try {
        if (!this.hasDayLevelData) {
          console.warn('No day level data available to download')
          return
        }

        // Compile cohort day level data
        const downloadOptions = { curve_features: false, day_level_data: true, processing_summary: false }
        const compiledData = compileCohortDataForDownload(
          {
            summary_stats: this.processingResults?.summary_stats,
            processed_subjects: this.processingResults?.processed_subjects || [],
            failed_subjects: this.processingResults?.failed_subjects || [],
            cohort_files: this.cohortFiles,
            processing_settings: this.processingSettings
          },
          downloadOptions,
          this.cohortColumnMetadata
        )

        if (!compiledData.day_level_data || compiledData.day_level_data.length === 0) {
          console.warn('No day level data compiled')
          return
        }

        // Convert array to worksheet
        const worksheet = XLSX.utils.json_to_sheet(compiledData.day_level_data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Day Level Data')

        // Generate filename
        const cohortId = `cohort_${new Date().toISOString().split('T')[0]}`
        const filename = `${cohortId}_day_level_data.xlsx`

        // Download the file
        XLSX.writeFile(workbook, filename)
        console.log('✅ Cohort day level data downloaded:', filename)
      } catch (error) {
        console.error('❌ Failed to download cohort day level data:', error)
        alert('Failed to download day level data. Please try again.')
      }
    },

    downloadProcessingSummary() {
      try {
        const summaryStats = this.processingResults?.summary_stats
        if (!summaryStats || Object.keys(summaryStats).length === 0) {
          console.warn('No processing summary available to download')
          return
        }

        downloadCohortProcessingSummary(this.processingResults, this.cohortColumnMetadata || {})
        console.log('✅ Cohort processing summary downloaded')
      } catch (error) {
        console.error('❌ Failed to download cohort processing summary:', error)
        alert('Failed to download processing summary. Please try again.')
      }
    },

    rerunCohortAnalysis() {
      // Simply open the process settings modal without changing the current state
      this.showProcessSettingsModal = true
      console.log('Rerun Cohort Analysis clicked - opening process settings modal')
    }
  },
  mounted() {
    // Set up back button to return to cohort upload with warning modal
    // Set up back button to return to cohort upload page
    // Note: No confirmation needed - processed data is saved in S3
    this.setBackButton({
      text: 'Back to Cohort Upload',
      route: '/upload/cohort' // Navigate directly to cohort upload page
    })
    
    console.log('CohortDashboard mounted with cohort files:', this.cohortFiles)
    
    // Load existing processing results if available
    if (this.globalState.cohortProcessingResults) {
      this.processingResults = this.globalState.cohortProcessingResults
      this.showStatusBar = true
      this.processing = false
      console.log('Loaded existing cohort processing results:', this.processingResults)
    }
  },

  beforeDestroy() {
    // Clear cohort processing results when navigating away
    // This ensures clean state when returning to upload
    if (this.processingResults) {
      console.log('🧹 CohortDashboard: Clearing processing results before navigation')
      this.updateGlobalState({
        cohortProcessingResults: null
      })
      sessionStorage.removeItem('cohortProcessingResults')
    }
  }
}
</script>
